/**
 * FormEntriesService
 * Admin surface for form entries: query, detail, answer edits, status actions,
 * manual payment, bulk actions, export paging and member conversion.
 * Route: forms/api/form-entries
 */
import { BaseService } from '../base/BaseService';
import { unwrapFormsResponse as unwrap } from './unwrapFormsResponse';
/** Admin form-entries service (JWT). */
export class FormEntriesService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? FormEntriesService.DEFAULT_SERVICE_PATH,
            basePath: FormEntriesService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ---- read ----
    /** POST api/form-entries/query — paged, row-scoped entry list. */
    async query(request = {}) {
        return unwrap(await this.queryRaw(request), 'Failed to query form entries');
    }
    queryRaw(request = {}) {
        return this.http.post(this.buildUrl('query'), request);
    }
    /** GET api/form-entries/{id} */
    async getById(id) {
        return unwrap(await this.getByIdRaw(id), `Form entry ${id} not found`);
    }
    getByIdRaw(id) {
        return this.http.get(this.buildUrl(String(id)));
    }
    // ---- edit ----
    /** PUT api/form-entries/{id}/answers */
    async updateAnswers(id, request) {
        return unwrap(await this.updateAnswersRaw(id, request), `Failed to update answers for entry ${id}`);
    }
    updateAnswersRaw(id, request) {
        return this.http.put(this.buildUrl(`${id}/answers`), request);
    }
    // ---- status actions ----
    statusAction(id, action, request) {
        return this.http.post(this.buildUrl(`${id}/${action}`), request ?? {});
    }
    /** POST api/form-entries/{id}/approve */
    async approve(id, request) {
        return unwrap(await this.approveRaw(id, request), `Failed to approve entry ${id}`);
    }
    approveRaw(id, request) {
        return this.statusAction(id, 'approve', request);
    }
    /** POST api/form-entries/{id}/reject */
    async reject(id, request) {
        return unwrap(await this.rejectRaw(id, request), `Failed to reject entry ${id}`);
    }
    rejectRaw(id, request) {
        return this.statusAction(id, 'reject', request);
    }
    /** POST api/form-entries/{id}/cancel */
    async cancel(id, request) {
        return unwrap(await this.cancelRaw(id, request), `Failed to cancel entry ${id}`);
    }
    cancelRaw(id, request) {
        return this.statusAction(id, 'cancel', request);
    }
    /** POST api/form-entries/{id}/confirm */
    async confirm(id, request) {
        return unwrap(await this.confirmRaw(id, request), `Failed to confirm entry ${id}`);
    }
    confirmRaw(id, request) {
        return this.statusAction(id, 'confirm', request);
    }
    /** POST api/form-entries/{id}/promote — promote from the waitlist. */
    async promote(id, request) {
        return unwrap(await this.promoteRaw(id, request), `Failed to promote entry ${id}`);
    }
    promoteRaw(id, request) {
        return this.statusAction(id, 'promote', request);
    }
    /** POST api/form-entries/{id}/resend-confirmation */
    async resendConfirmation(id, request) {
        return unwrap(await this.resendConfirmationRaw(id, request), `Failed to resend confirmation for entry ${id}`);
    }
    resendConfirmationRaw(id, request) {
        return this.statusAction(id, 'resend-confirmation', request);
    }
    /** POST api/form-entries/{id}/manual-payment — record cash/EFT or waive the balance. */
    async manualPayment(id, request) {
        return unwrap(await this.manualPaymentRaw(id, request), `Failed to record manual payment for entry ${id}`);
    }
    manualPaymentRaw(id, request) {
        return this.http.post(this.buildUrl(`${id}/manual-payment`), request);
    }
    /** DELETE api/form-entries/{id} */
    async remove(id) {
        const response = await this.removeRaw(id);
        return response.data ?? response.success;
    }
    removeRaw(id) {
        return this.http.delete(this.buildUrl(String(id)));
    }
    // ---- bulk / export ----
    /** POST api/form-entries/bulk */
    async bulk(request) {
        return unwrap(await this.bulkRaw(request), 'Bulk entry action failed');
    }
    bulkRaw(request) {
        return this.http.post(this.buildUrl('bulk'), request);
    }
    /** POST api/form-entries/export — one page (pageSize ≤ 1000); the client builds the file. */
    async exportPage(request) {
        return unwrap(await this.exportPageRaw(request), 'Failed to export form entries');
    }
    exportPageRaw(request) {
        return this.http.post(this.buildUrl('export'), request);
    }
    // ---- member conversion ----
    /** GET api/form-entries/{id}/member-candidates */
    async getMemberCandidates(id) {
        return unwrap(await this.getMemberCandidatesRaw(id), `Member candidates for entry ${id} not found`);
    }
    getMemberCandidatesRaw(id) {
        return this.http.get(this.buildUrl(`${id}/member-candidates`));
    }
    /** POST api/form-entries/{id}/convert — create a member/visitor from the entry. */
    async convert(id, request = {}) {
        return unwrap(await this.convertRaw(id, request), `Failed to convert entry ${id}`);
    }
    convertRaw(id, request = {}) {
        return this.http.post(this.buildUrl(`${id}/convert`), request);
    }
    /** POST api/form-entries/{id}/merge — link + write the ticked fields onto an existing member. */
    async merge(id, request) {
        return unwrap(await this.mergeRaw(id, request), `Failed to merge entry ${id}`);
    }
    mergeRaw(id, request) {
        return this.http.post(this.buildUrl(`${id}/merge`), request);
    }
    /** POST api/form-entries/{id}/link — link only (no field writes). */
    async link(id, request) {
        return unwrap(await this.linkRaw(id, request), `Failed to link entry ${id}`);
    }
    linkRaw(id, request) {
        return this.http.post(this.buildUrl(`${id}/link`), request);
    }
    /** POST api/form-entries/bulk-convert */
    async bulkConvert(request) {
        return unwrap(await this.bulkConvertRaw(request), 'Bulk convert failed');
    }
    bulkConvertRaw(request) {
        return this.http.post(this.buildUrl('bulk-convert'), request);
    }
}
FormEntriesService.DEFAULT_SERVICE_PATH = 'forms';
FormEntriesService.BASE_PATH = 'api/form-entries';
//# sourceMappingURL=FormEntriesService.js.map